/**
 * Share codes (docs/spec/03 §6): a cave as `SF1.` + base64url of a compact bit stream, short
 * enough for a URL. Pure. Decoding never throws; a bad code gives a reason instead.
 *
 * Stream: version (a whole byte), flags, width, height, the three colours, diamond and extra
 * value, amoeba and magic wall times (when present), the random fill, the levels (seed, quota,
 * time, each after the first as a change from the one before; tick), id, name, hint, the map
 * (`codecMap.ts`), zero bits to a byte boundary, then a CRC-16 of every byte before it. Fill
 * percentages are kept in tenths. A map with an all-steel border stores only its inside.
 */

import { BadCode, BitReader, BitWriter, crc16, fromBase64Url, toBase64Url } from './codecBits';
import { readCells, readSymbol, writeCells, writeSymbol } from './codecMap';
import type { CaveDef, CaveFill, CaveLevel } from './types';

export { crc16, fromBase64Url, toBase64Url } from './codecBits';

export const CODE_PREFIX = 'SF1.';
const VERSION = 1;

const F_INTERMISSION = 1;
const F_AMOEBA = 2;
const F_MAGIC = 4;
const F_FILL = 8;
const F_STEEL_BORDER = 16;

const MAX_SIDE = 255;
const MAX_TEXT = 1000;

export type DecodeResult = { ok: true; def: CaveDef } | { ok: false; reason: string };

/** The cell at (x, y) as `buildCave` sees it: short rows are padded with steel. */
function cellOf(def: CaveDef, x: number, y: number): string {
  return def.map[y]?.[x] ?? 'W';
}

function steelBorder(def: CaveDef, w: number, h: number): boolean {
  if (w < 3 || h < 3) return false;
  for (let x = 0; x < w; x++) if (cellOf(def, x, 0) !== 'W' || cellOf(def, x, h - 1) !== 'W') return false;
  for (let y = 0; y < h; y++) if (cellOf(def, 0, y) !== 'W' || cellOf(def, w - 1, y) !== 'W') return false;
  return true;
}

function writeLevels(out: BitWriter, levels: readonly CaveLevel[]): void {
  out.int(levels.length);
  let prev: CaveLevel = { seed: 0, diamonds: 0, time: 0 };
  for (const lv of levels) {
    out.int(lv.seed - prev.seed);
    out.int(lv.diamonds - prev.diamonds);
    out.int(lv.time - prev.time);
    out.int(lv.tick === undefined ? 0 : lv.tick + 1);
    prev = lv;
  }
}

function readLevels(r: BitReader): CaveLevel[] {
  const levels: CaveLevel[] = [];
  const n = r.count(64, 'levels');
  let prev: CaveLevel = { seed: 0, diamonds: 0, time: 0 };
  for (let i = 0; i < n; i++) {
    const seed = prev.seed + r.int();
    const diamonds = prev.diamonds + r.int();
    const time = prev.time + r.int();
    const tick = r.int();
    prev = tick === 0 ? { seed, diamonds, time } : { seed, diamonds, time, tick: tick - 1 };
    levels.push(prev);
  }
  return levels;
}

/** A cave as a share code: `SF1.` + base64url. A 40 x 22 cave is typically 150-400 characters. */
export function encodeCave(def: CaveDef): string {
  const out = new BitWriter();
  const w = def.map.reduce((n, row) => Math.max(n, row.length), 0);
  const h = def.map.length;
  const border = steelBorder(def, w, h);
  let flags = def.intermission ? F_INTERMISSION : 0;
  if (def.amoebaTime !== undefined) flags |= F_AMOEBA;
  if (def.magicWallTime !== undefined) flags |= F_MAGIC;
  if (def.fill !== undefined) flags |= F_FILL;
  if (border) flags |= F_STEEL_BORDER;
  out.bits(VERSION, 8);
  out.bits(flags, 5);
  out.int(w);
  out.int(h);
  for (const c of def.colors) out.int(c);
  out.int(def.diamondValue);
  out.int(def.extraValue);
  if (def.amoebaTime !== undefined) out.int(def.amoebaTime);
  if (def.magicWallTime !== undefined) out.int(def.magicWallTime);
  if (def.fill) {
    writeSymbol(out, def.fill.base);
    out.int(def.fill.items.length);
    for (const [ch, percent] of def.fill.items) {
      writeSymbol(out, ch);
      out.int(percent * 10);
    }
  }
  writeLevels(out, def.levels);
  out.text(def.id);
  out.text(def.name);
  out.text(def.hint);
  const inset = border ? 1 : 0;
  const cells: string[] = [];
  for (let y = inset; y < h - inset; y++) for (let x = inset; x < w - inset; x++) cells.push(cellOf(def, x, y));
  writeCells(out, cells);
  const bytes = out.bytes();
  const crc = crc16(Uint8Array.from(bytes));
  return CODE_PREFIX + toBase64Url(Uint8Array.from([...bytes, crc >> 8, crc & 255]));
}

function readMap(r: BitReader, w: number, h: number, inset: number): string[] {
  const iw = w - inset * 2;
  const cells = readCells(r, iw * (h - inset * 2));
  const rows: string[] = [];
  for (let y = 0; y < h; y++) {
    if (inset === 1 && (y === 0 || y === h - 1)) rows.push('W'.repeat(w));
    else {
      const iy = y - inset;
      const inner = cells.slice(iy * iw, iy * iw + iw).join('');
      rows.push(inset === 1 ? `W${inner}W` : inner);
    }
  }
  return rows;
}

function readDef(r: BitReader): CaveDef {
  const version = r.bits(8);
  if (version !== VERSION) throw new BadCode(`the code was made by a newer STONEFALL (version ${version})`);
  const flags = r.bits(5);
  const w = r.count(MAX_SIDE, 'width');
  const h = r.count(MAX_SIDE, 'height');
  const inset = flags & F_STEEL_BORDER ? 1 : 0;
  if (w < 1 || h < 1 || (inset === 1 && (w < 3 || h < 3))) throw new BadCode('the code is damaged (size)');
  const colors = [r.int(), r.int(), r.int()] as const;
  const diamondValue = r.int();
  const extraValue = r.int();
  const amoebaTime = flags & F_AMOEBA ? r.int() : undefined;
  const magicWallTime = flags & F_MAGIC ? r.int() : undefined;
  let fill: CaveFill | undefined;
  if (flags & F_FILL) {
    const base = readSymbol(r);
    const n = r.count(64, 'fill');
    const items: [string, number][] = [];
    for (let i = 0; i < n; i++) items.push([readSymbol(r), r.int() / 10]);
    fill = { base, items };
  }
  const levels = readLevels(r);
  const id = r.text(MAX_TEXT);
  const name = r.text(MAX_TEXT);
  const hint = r.text(MAX_TEXT);
  const map = readMap(r, w, h, inset);
  return {
    id,
    name,
    hint,
    intermission: (flags & F_INTERMISSION) !== 0,
    colors,
    diamondValue,
    extraValue,
    ...(amoebaTime === undefined ? {} : { amoebaTime }),
    ...(magicWallTime === undefined ? {} : { magicWallTime }),
    ...(fill === undefined ? {} : { fill }),
    levels,
    map,
  };
}

/**
 * A share code (or a link or text containing one) back to a cave. Never throws. It checks that the
 * code is intact, not that the cave is playable: run `validateCave` on the result for that.
 */
export function decodeCave(code: string): DecodeResult {
  try {
    const found = /SF(\d+)\.([A-Za-z0-9_-]*)/.exec(typeof code === 'string' ? code : '');
    if (!found) return { ok: false, reason: 'not a STONEFALL cave code' };
    if (found[1] !== '1') return { ok: false, reason: `the code was made by a newer STONEFALL (SF${found[1] ?? '?'})` };
    const bytes = fromBase64Url(found[2] ?? '');
    if (!bytes) return { ok: false, reason: 'the code is damaged (bad characters or length)' };
    if (bytes.length < 6) return { ok: false, reason: 'the code is cut short' };
    const end = bytes.length - 2;
    const stored = ((bytes[end] ?? 0) << 8) | (bytes[end + 1] ?? 0);
    if (crc16(bytes, end) !== stored) return { ok: false, reason: 'the code is damaged or incomplete (checksum)' };
    const r = new BitReader(bytes, end);
    const def = readDef(r);
    if (!r.atEnd()) return { ok: false, reason: 'the code is damaged (extra data)' };
    return { ok: true, def };
  } catch (e) {
    return { ok: false, reason: e instanceof BadCode ? e.message : 'the code could not be read' };
  }
}
